import * as THREE from 'three';
import { loadModel } from '../MeshHelpers';
import {
  get,
  getColorOrNull,
  getOrNull,
  subtractObjects,
} from '../window/WindowHelpers';
import * as util from '../util';

function addOutline(threeObject) {
  threeObject.traverse(obj => {
    if (obj instanceof THREE.Mesh) {
      obj.geometry.computeVertexNormals();
      obj.material.polygonOffset = true;
      obj.material.polygonOffsetFactor = -1;
      obj.material.polygonOffsetUnits = -3;
      obj.buildEdges();
    }
  });
}

export function buildHinging(def, container, d, onDone) {
  const w = d.w;
  const h = d.h;
  const innerBorderAdjust = d.innerBorderAdjust;

  const side = get(def, 'Side'); // Seen from the outside
  const type = getOrNull(def, 'Type') || 'Regular';

  if (['Regular', 'SlidingDoor', 'Loop', 'Drop'].includes(type)) {
    loadModel(type + 'Handle.glb', model => {
      const alignToFrameAndDouble = type === 'Loop' || type === 'Drop';
      if (type === 'SlidingDoor') {
        model.scale.multiplyScalar(0.025);
      } else if (type === 'Loop' || type === 'Drop') {
        model.scale.multiplyScalar(0.07);
      }

      addOutline(model);

      let posX = 0;
      let posY = 0;
      if (side === 'Left' || (alignToFrameAndDouble && side === 'Bottom')) {
        model.scale.x *= -1;
        posX = -w / 2 - innerBorderAdjust.VSP - d.sashBorderInnerWidth.VSP;
        if (!alignToFrameAndDouble)
          posX = posX + d.sashBorderInnerWidth.VSP / 2;
      } else if (side === 'Right') {
        posX = w / 2 + innerBorderAdjust.HSP + d.sashBorderInnerWidth.HSP;
        if (!alignToFrameAndDouble)
          posX = posX - d.sashBorderInnerWidth.HSP / 2;
      } else if (side === 'Top') {
        model.rotateZ((Math.PI * 3) / 2);
        posY = h / 2 + innerBorderAdjust.OVP + d.sashBorderInnerWidth.OVP;
        if (!alignToFrameAndDouble)
          posY = posY - d.sashBorderInnerWidth.OVP / 2;
      } else if (side === 'Bottom') {
        model.rotateZ(Math.PI / 2);
        posY = -h / 2 - innerBorderAdjust.UNP - d.sashBorderInnerWidth.UNP;
        if (!alignToFrameAndDouble)
          posY = posY + d.sashBorderInnerWidth.UNP / 2;
      }
      const posZ = -d.b / 2;

      const object = new THREE.Object3D();
      object.position.x = posX;
      object.position.y = posY;
      object.position.z = posZ;
      object.rotation.y = Math.PI;

      if (alignToFrameAndDouble) {
        const object2 = object.clone();
        const modelClone = model.clone();
        if (['Left', 'Right'].includes(side)) {
          object.position.y = h / 4;
          object2.position.y = -h / 4;
          object2.add(modelClone);
        } else if (['Bottom'].includes(side)) {
          object.position.y = -h / 3;
          object2.position.y = -h / 3;
          object2.position.x = -object.position.x;
          modelClone.scale.x *= -1;
          object2.add(modelClone);
        }
        container.add(object2);
      }
      object.add(model);
      container.add(object);
      onDone();
    });
  } else {
    throw 'Invalid handle type';
  }
}

function buildAccessoriesHolder(container, width, height, material) {
  const meshOverflow = 0.025;
  const meshHeight = 0.025;
  const meshDepth = 0.012;
  const geometry = new THREE.BoxGeometry(
    width + meshOverflow * 2,
    meshHeight,
    meshDepth
  );
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.setY(height / 2 + meshHeight / 2);
  mesh.buildEdges();
  container.add(mesh);
  return meshDepth;
}

function buildBlindPrimaryMesh(container, width, height, color) {
  const segmentWidth = 0.016;
  const segmentDepth = 0.001;
  const segmentSpace = 0.015;

  const material = new THREE.MeshLambertMaterial({
    color: color,
  });
  for (let y = 0; y < height / 2; y += segmentSpace) {
    const geo = new THREE.BoxGeometry(width, segmentWidth, segmentDepth);
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.y += height / 2 - y;
    mesh.rotation.x += Math.PI / 2.7;
    mesh.buildEdges();
    container.add(mesh);
  }
}

function buildBlindLines(container, width, height) {
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
  });
  const lineRadius = 0.0005;

  const line1 = new THREE.Mesh(
    new THREE.CylinderGeometry(lineRadius, lineRadius, height / 2, 4, 1),
    material
  );
  line1.position.setX(-width * 0.4);
  line1.position.setY(height / 4);
  line1.buildEdges();
  container.add(line1);

  const line2 = new THREE.Mesh(
    new THREE.CylinderGeometry(lineRadius, lineRadius, height / 2, 4, 1),
    material
  );
  line2.position.setX(width * 0.4);
  line2.position.setY(height / 4);
  line2.buildEdges();
  container.add(line2);
}

function buildBlindAdjuster(container, width, height, side, onLoad) {
  loadModel('WindowBlindAdjuster.glb', model => {
    addOutline(model);

    model.scale.multiplyScalar(0.1);
    model.position.setX((width / 2) * side * -1);
    model.position.setY(height / 2);
    model.rotateY(Math.PI);
    if (side < 0) {
      model.scale.x *= -1;
    }
    container.add(model);
    onLoad();
  });
}

export function buildBlind(def, container, d) {
  const width =
    d.w + (d.innerBorderAdjust.VSP || 0) + (d.innerBorderAdjust.HSP || 0);
  const height =
    d.h + (d.innerBorderAdjust.OVP || 0) + (d.innerBorderAdjust.UNP || 0);
  const dx =
    ((d.innerBorderAdjust.HSP || 0) - (d.innerBorderAdjust.VSP || 0)) / 2;
  const dy =
    ((d.innerBorderAdjust.OVP || 0) - (d.innerBorderAdjust.UNP || 0)) / 2;
  const color = util.hexToNumber(get(def, 'Key'));
  const sideString = getOrNull(def, 'Side');
  const side = sideString
    ? sideString.toLowerCase() === 'left'
      ? -1
      : sideString.toLowerCase() === 'right'
      ? 1
      : undefined
    : undefined;

  const object = new THREE.Object3D();
  const depth = buildAccessoriesHolder(object, width, height, d.treeMaterial);
  buildBlindPrimaryMesh(object, width, height, color);
  buildBlindLines(object, width, height);
  if (side) {
    buildBlindAdjuster(object, width, height, side, () => {
      if (this._renderer) {
        this._renderer.requireRenderUpdate = true;
      }
    });
  }

  object.position.setX(dx);
  object.position.setY(dy);
  object.position.setZ(-d.b / 2 - depth / 2);

  container.add(object);
}

function buildPlissePrimaryMesh(container, width, height, color) {
  const geometry = new THREE.Geometry();
  const preferredSegmentLength = 0.02;
  const meshHeight = height / 2;
  const segments = Math.max(1, Math.round(meshHeight / preferredSegmentLength));
  const segmentLength = meshHeight / segments;
  const depth = segmentLength / 2;
  const thickness = 0.001;
  const horizontalThickness = Math.sqrt(thickness * thickness * 2);

  geometry.vertices.push(
    new THREE.Vector3(
      -width / 2,
      height / 2,
      depth / 2 - horizontalThickness / 2
    ),
    new THREE.Vector3(
      width / 2,
      height / 2,
      depth / 2 - horizontalThickness / 2
    ),
    new THREE.Vector3(
      -width / 2,
      height / 2,
      depth / 2 + horizontalThickness / 2
    ),
    new THREE.Vector3(
      width / 2,
      height / 2,
      depth / 2 + horizontalThickness / 2
    )
  );
  geometry.faces.push(new THREE.Face3(0, 2, 1), new THREE.Face3(1, 2, 3));
  for (let i = 1; i <= segments; i++) {
    geometry.vertices.push(
      new THREE.Vector3(
        -width / 2,
        height / 2 - segmentLength * i + segmentLength * 0.5,
        -depth / 2 - horizontalThickness / 2
      ),
      new THREE.Vector3(
        width / 2,
        height / 2 - segmentLength * i + segmentLength * 0.5,
        -depth / 2 - horizontalThickness / 2
      ),
      new THREE.Vector3(
        -width / 2,
        height / 2 - segmentLength * i + segmentLength * 0.5,
        -depth / 2 + horizontalThickness / 2
      ),
      new THREE.Vector3(
        width / 2,
        height / 2 - segmentLength * i + segmentLength * 0.5,
        -depth / 2 + horizontalThickness / 2
      ),
      new THREE.Vector3(
        -width / 2,
        height / 2 - segmentLength * i,
        depth / 2 - horizontalThickness / 2
      ),
      new THREE.Vector3(
        width / 2,
        height / 2 - segmentLength * i,
        depth / 2 - horizontalThickness / 2
      ),
      new THREE.Vector3(
        -width / 2,
        height / 2 - segmentLength * i,
        depth / 2 + horizontalThickness / 2
      ),
      new THREE.Vector3(
        width / 2,
        height / 2 - segmentLength * i,
        depth / 2 + horizontalThickness / 2
      )
    );
    const lv = geometry.vertices.length;
    geometry.faces.push(
      // Front
      new THREE.Face3(lv - 12, lv - 11, lv - 8),
      new THREE.Face3(lv - 11, lv - 7, lv - 8),
      new THREE.Face3(lv - 8, lv - 7, lv - 4),
      new THREE.Face3(lv - 7, lv - 3, lv - 4),

      // Back
      new THREE.Face3(lv - 10, lv - 6, lv - 9),
      new THREE.Face3(lv - 9, lv - 6, lv - 5),
      new THREE.Face3(lv - 6, lv - 2, lv - 5),
      new THREE.Face3(lv - 5, lv - 2, lv - 1),

      // Left
      new THREE.Face3(lv - 12, lv - 8, lv - 10),
      new THREE.Face3(lv - 10, lv - 8, lv - 6),
      new THREE.Face3(lv - 8, lv - 4, lv - 6),
      new THREE.Face3(lv - 6, lv - 4, lv - 2),

      // Right
      new THREE.Face3(lv - 11, lv - 9, lv - 7),
      new THREE.Face3(lv - 9, lv - 5, lv - 7),
      new THREE.Face3(lv - 7, lv - 5, lv - 3),
      new THREE.Face3(lv - 5, lv - 1, lv - 3)
    );
  }
  const lv = geometry.vertices.length;
  geometry.faces.push(
    new THREE.Face3(lv - 4, lv - 3, lv - 2),
    new THREE.Face3(lv - 3, lv - 1, lv - 2)
  );

  const material = new THREE.MeshPhongMaterial({
    color: util.darkenColor(color, 0.15),
    flatShading: true,
    shininess: 0,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.buildEdges();
  container.add(mesh);

  return depth;
}

function buildPlisseLines(container, width, height) {
  const lineMaterial = new THREE.MeshBasicMaterial({
    color: 0xcccccc,
  });
  const lineRadius = 0.0005;
  const lineHeight = height / 2;

  const line1 = new THREE.Mesh(
    new THREE.CylinderGeometry(lineRadius, lineRadius, lineHeight, 4, 1),
    lineMaterial
  );
  line1.position.setX(-width * 0.4);
  line1.position.setY(lineHeight / 2);
  line1.buildEdges();
  container.add(line1);

  const line2 = new THREE.Mesh(
    new THREE.CylinderGeometry(lineRadius, lineRadius, lineHeight, 4, 1),
    lineMaterial
  );
  line2.position.setX(width * 0.4);
  line2.position.setY(lineHeight / 2);
  line2.buildEdges();
  container.add(line2);

  const line3 = new THREE.Mesh(
    new THREE.CylinderGeometry(lineRadius, lineRadius, lineHeight, 4, 1),
    lineMaterial
  );
  line3.position.setY(lineHeight / 2);
  line3.buildEdges();
  container.add(line3);
}

export function buildPlisse(def, container, d) {
  const width =
    d.w + (d.innerBorderAdjust.VSP || 0) + (d.innerBorderAdjust.HSP || 0);
  const height =
    d.h + (d.innerBorderAdjust.OVP || 0) + (d.innerBorderAdjust.UNP || 0);
  const dx =
    ((d.innerBorderAdjust.HSP || 0) - (d.innerBorderAdjust.VSP || 0)) / 2;
  const dy =
    ((d.innerBorderAdjust.OVP || 0) - (d.innerBorderAdjust.UNP || 0)) / 2;
  const color = util.hexToNumber(get(def, 'Key'));
  const sideString = getOrNull(def, 'Side');
  const side = sideString
    ? sideString.toLowerCase() === 'left'
      ? -1
      : sideString.toLowerCase() === 'right'
      ? 1
      : undefined
    : undefined;

  const object = new THREE.Object3D();
  const depth = buildAccessoriesHolder(object, width, height, d.treeMaterial);
  buildPlissePrimaryMesh(object, width, height, color);
  buildPlisseLines(object, width, height);
  if (side) {
    buildBlindAdjuster(object, width, height, side, () => {
      if (this._renderer) {
        this._renderer.requireRenderUpdate = true;
      }
    });
  }

  object.position.setX(dx);
  object.position.setY(dy);
  object.position.setZ(-d.b / 2 - depth / 2);
  container.add(object);
}

function createMosquitoNetMesh(width, height, color, sizeMultiplier) {
  const container = new THREE.Object3D();
  const preferredGapSize = 0.005 * sizeMultiplier;
  const threadRadius = 0.0003 * sizeMultiplier;
  const cylinderSegments = 4;
  if (color === null) {
    color = 0x333333;
  }

  const material = new THREE.MeshBasicMaterial({
    color: color,
  });

  const hGaps = Math.round(
    (width - preferredGapSize) / (threadRadius * 2 + preferredGapSize)
  );
  const hGapSize = (width - threadRadius * 2 * (hGaps - 1)) / hGaps;
  for (let i = 1; i < hGaps; i++) {
    const geometry = new THREE.CylinderGeometry(
      threadRadius,
      threadRadius,
      height,
      cylinderSegments,
      1
    );
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.setX(
      -width / 2 + hGapSize * i + (i - 1) * threadRadius * 2 + threadRadius
    );
    container.add(mesh);
  }

  const vGaps = Math.round(
    (height - preferredGapSize) / (threadRadius * 2 + preferredGapSize)
  );
  const vGapSize = (height - threadRadius * 2 * (vGaps - 1)) / vGaps;
  for (let i = 1; i < vGaps; i++) {
    const geometry = new THREE.CylinderGeometry(
      threadRadius,
      threadRadius,
      width,
      cylinderSegments,
      1
    );
    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotateZ(Math.PI / 2);
    mesh.position.setY(
      -height / 2 + vGapSize * i + (i - 1) * threadRadius * 2 + threadRadius
    );
    container.add(mesh);
  }
  return container;
}

function buildMosquitoNetPrimaryMesh(container, width, height, color) {
  const mesh1 = createMosquitoNetMesh(width, height, color, 1);
  const mesh2 = createMosquitoNetMesh(width, height, color, 2);
  const mesh3 = createMosquitoNetMesh(width, height, color, 3);
  const mesh4 = createMosquitoNetMesh(width, height, color, 4);
  const mesh5 = createMosquitoNetMesh(width, height, color, 6);
  container.add(mesh1);
  container.add(mesh2);
  container.add(mesh3);
  container.add(mesh4);
  container.add(mesh5);

  // Due to unwanted moiré patterns in the mosquito net on small and/or zoomed out screens,
  // we create multiple nets and only display the one with the most appropriate size based
  // on how big it appears on the screen.
  const onChange = () => {
    const { renderer, camera } = this._renderer;
    const rendererSize = renderer.getSize(new THREE.Vector2());
    const camValue =
      camera && camera.obj instanceof THREE.PerspectiveCamera
        ? camera.getCameraDistance() / 0.8
        : 1;
    const heightValue = 1000 / rendererSize.height;
    const mul = (camValue + heightValue) / 2;
    mesh1.visible = false;
    mesh2.visible = false;
    mesh3.visible = false;
    mesh4.visible = false;
    mesh5.visible = false;
    if (mul < 1) {
      mesh1.visible = true;
    } else if (mul < 2) {
      mesh2.visible = true;
    } else if (mul < 3) {
      mesh3.visible = true;
    } else if (mul < 4) {
      mesh4.visible = true;
    } else {
      mesh5.visible = true;
    }
  };
  onChange();

  this.onCameraZoomEvents.push(onChange);
  this.onRendererResizeEvents.push(onChange);
}

export function buildMosquitoNet(def, container, d) {
  const width =
    d.w + (d.innerBorderAdjust.VSP || 0) + (d.innerBorderAdjust.HSP || 0);
  const height =
    d.h + (d.innerBorderAdjust.OVP || 0) + (d.innerBorderAdjust.UNP || 0);
  const dx =
    ((d.innerBorderAdjust.HSP || 0) - (d.innerBorderAdjust.VSP || 0)) / 2;
  const dy =
    ((d.innerBorderAdjust.OVP || 0) - (d.innerBorderAdjust.UNP || 0)) / 2;
  const netColor = getColorOrNull(def, 'Key');

  const object = new THREE.Object3D();

  const depth = buildAccessoriesHolder(object, width, height, d.treeMaterial);
  buildMosquitoNetPrimaryMesh.call(this, object, width, height, netColor);

  object.position.setX(dx);
  object.position.setY(dy);
  object.position.setZ(-d.b / 2 - depth / 2);

  container.add(object);
}
